import { ErrorOutline, Home, Refresh } from '@mui/icons-material'
import { Box, Button, Container, Paper, Typography } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

interface GlobalErrorProps {
  error?: Error
  errorInfo?: {
    componentStack?: string
  }
  resetError?: () => void
  showResetButton?: boolean
  showHomeButton?: boolean
}

export const GlobalError = ({
  error,
  errorInfo,
  resetError,
  showResetButton = true,
  showHomeButton = true,
}: GlobalErrorProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleGoHome = () => {
    navigate({ to: '/' })
  }

  const handleReload = () => {
    window.location.reload()
  }

  const handleReset = () => {
    if (resetError) {
      resetError()
    } else {
      handleReload()
    }
  }

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
        gap={3}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            borderRadius: 2,
            maxWidth: 600,
            width: '100%',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <ErrorOutline
              sx={{
                fontSize: 80,
                color: 'error.main',
              }}
            />

            <Box>
              <Typography variant="h4" component="h1" gutterBottom color="error">
                {t('Произошла ошибка')}
              </Typography>

              <Typography variant="body1" color="text.secondary" paragraph>
                {error?.message || t('Что-то пошло не так. Пожалуйста, попробуйте еще раз.')}
              </Typography>
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              {showResetButton && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Refresh />}
                  onClick={handleReset}
                >
                  {t('Попробовать снова')}
                </Button>
              )}

              {showHomeButton && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Home />}
                  onClick={handleGoHome}
                >
                  {t('На главную')}
                </Button>
              )}
            </Box>

            {process.env.NODE_ENV === 'development' && error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {t('Детали ошибки (только в режиме разработки):')}
                </Typography>

                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {error.stack || error.message}
                </Typography>

                {errorInfo?.componentStack && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      {t('Стек компонентов:')}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {errorInfo.componentStack}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}
